<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="contact-dashboard">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #actions>
          <TaskCreateButton :reference="{ onModel: 'Contact', ref: id }" />
          <TaskButtonList v-model="item.tasks" class="ml-2" :reference-id="id" />
          <Actions placement="contact" :query="{ contact: id }" />
        </template>
      </HeaderComponent>
      <div class="contact-dashboard__content">
        <Comments :value="item.comments" @submit="comment" :promise="commentPromise" />
        <ContactCard :value="item" />
        <Contracts
          v-if="item.id"
          :reference="{ onModel: 'Contact', ref: item.id }"
          :default-status-filter="['!draft', '!declined']"
          :contact="item.id"
        />
        <Tasks :reference="{ onModel: 'Contact', ref: id }" />
      </div>
    </div>
    <ModalComponent :show="showDirector">
      <Card class="contact-dashboard__director" :title="$t('contactDirectorPrompt')">
        <ButtonComponent
          v-for="item in item.leads"
          data-part="director"
          tag="router-link"
          :to="getLeadRoute(item)"
          :style="{
            '--bg': `url(/api/lead/${item.id}/cover)`,
          }"
          :label="item.reference.name"
          icon="fa-pro-solid:arrow-right"
        />
        <ButtonComponent
          :label="$t('close')"
          icon="fa-pro-light:times"
          @click="showDirector = false"
          class="self-center"
        />
      </Card>
    </ModalComponent>
  </Loader>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import CommentApi from '@/api/Comment'
import { LeadPathByModel } from '@/components/Lead/utils'

import ContactCard from '@/components/Contact/Card'
import Contracts from '@/components/Contract/Contracts'
import Comments from '@/components/Comment/List'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import { ModalComponent } from 'vue-elder-modal'

import Tasks from '@/components/Task/List/Card'
import Actions from '@/components/Actions'

export default {
  props: {
    id: String,
  },
  data() {
    return {
      commentPromise: null,
      showDirector: false,
    }
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  computed: {
    ...mapState('Contact', ['item', 'promises']),
    ...mapi18n('i18n', {
      loading() {
        return this.$tc('loading') + '...'
      },
      loadingContact() {
        return this.$t('loading', { resource: this.$tc('contact', 1) }) + '...'
      },
    }),
    breadcrumbs() {
      return [{ title: this.$tc('contact', 2), action: { name: 'contacts' } }, { title: this.$metaInfo.title }]
    },
  },
  methods: {
    ...mapMutations('Contact', ['set', 'addComment']),
    ...mapActions('Contact', {
      fetch(dispatch) {
        if (!this.id) return
        return dispatch('fetch', [{ id: this.id }, { query: 'ContactDashboard' }]).then(() => {
          this.showDirector = this.item.leads.length > 0
        })
      },
    }),
    comment(data) {
      this.commentPromise = CommentApi.mutate({
        reference: { onModel: 'Contact', ref: this.id },
        ...data,
      }).then(data => this.addComment(data))
    },
    getLeadRoute(item) {
      return {
        name: `${LeadPathByModel[item.reference.onModel]}-lead`,
        params: { id: item.id, reference: item.reference.id },
      }
    },
  },
  metaInfo() {
    return {
      title: this.$path('item.name') || this.i18n.loadingContact,
    }
  },
  components: {
    HeaderComponent,
    ContactCard,
    Comments,
    Card,
    Actions,
    ModalComponent,
    Contracts,
    Tasks,
  },
}
</script>

<style lang="scss">
.contact-dashboard {
  background-color: $background-grey;

  &__content {
    display: grid;
    grid-gap: 2rem;
    grid-template-columns: 1fr 350px;
    grid-template-areas: 'comments contact' 'contracts contact';
    align-items: flex-start;

    .comments {
      grid-area: comments;
    }

    .contact-card {
      grid-area: contact;
    }

    .contract-list {
      grid-area: contracts;
    }
  }

  &__form {
    margin: 0 auto;
    max-width: 550px;
  }

  &__director {
    .card__content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  }

  [data-part='director'] {
    color: white;
    font-size: 1.5rem;
    font-weight: bold;

    min-width: 500px;

    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), var(--bg);
    background-position: center;
    background-size: cover;

    --vue-elder-button-padding-x: 2rem;
    --vue-elder-button-padding-y: 2rem;
  }
}
</style>
